import { useState, useEffect } from "react";
import DatePicker from "react-multi-date-picker";
import FilterBox from "src/components/FilterBox";
import PageHeader from "src/components/PageHeader";
import Table from "src/components/Table/Table";
import { get } from "src/services/HttpClient";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";
import { persianToEnglishDigitConverter } from "src/utils/persianToEnglishDigitConverter";
import { MdAccessTime } from "react-icons/md";


export default function SellersSales() {
    const [salesData, setSalesData] = useState();
    const [loading, setLoading] = useState(true);
    const [users, setUsers] = useState([]);
    const [selectedSeller, setSelectedSeller] = useState(null);
    const [timePeriod, setTimePeriod] = useState()

    useEffect(() => {
        getUsers();
    }, []);

    useEffect(() => {
        if (selectedSeller) {
            // پاک کردن اطلاعات قبلی و دریافت اطلاعات جدید
            setSalesData(undefined);
            fetchSellerSales(selectedSeller.email);
        }
    }, [selectedSeller]);

    const getUsers = async () => {
        try {
            const result = await get("/users");
            if (result?.success) {
                // فیلتر کردن فقط کاربران با نقش seller
                const sellers = result.body.filter(user => user.role?.role === "seller");
                setUsers(sellers);

                // اگر سلر وجود دارد، اولین مورد را انتخاب کن
                if (sellers.length > 0) {
                    setSelectedSeller(sellers[0]);
                }
            }
        } catch (err) {
            console.error("Error fetching users:", err);
        }
    };

    const fetchSellerSales = async (sellerEmail) => {
        try {
            setLoading(true);
            const response = await get(`/seller/orders/${sellerEmail}`);

            if (response?.success) {
                setSalesData(response.body);
            }
        } catch (err) {
            console.error("Error fetching seller sales:", err);
        } finally {
            setLoading(false);
        }
    };

    const formatPrice = (price) => {
        return new Intl.NumberFormat("fa-IR").format(price) + " تومان";
    };

    const formatDate = (dateString) => {
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString("fa-IR", {
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
                hour: "2-digit",
                minute: "2-digit"
            });
        } catch {
            return dateString;
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case "wc-completed":
                return "bg-green-100 text-green-800";
            case "wc-completed-service":
                return "bg-green-100 text-green-800";
            case "wc-processing":
                return "bg-yellow-100 text-yellow-800";
            case "wc-failed":
                return "bg-red-100 text-red-800";
            case "wc-cancelled":
                return "bg-gray-100 text-gray-800";
            case "wc-pws-courier":
            case "wc-pws-post":
                return "bg-blue-100 text-blue-800";
            case "wc-pws-packaged":
                return "bg-blue-100 text-blue-800";
            default:
                return "bg-gray-100 text-gray-800";
        }
    };

    const getStatusText = (status) => {
        switch (status) {
            case "wc-completed":
                return "تکمیل شده";
            case "wc-processing":
                return "در حال پردازش";
            case "wc-failed":
                return "ناموفق";
            case "wc-cancelled":
                return "لغو شده";
            case "wc-pws-courier":
                return "ارسال با پیک";
            case "wc-pws-packaged":
                return "بسته بندی شده";
            case "wc-pws-post":
                return "ارسال با پست";
            default:
                return status;
        }
    };

    const columns = [
        {
            id: "product_image",
            cell: (info) => (<img className="w-[80px] max-w-[80px] rounded-lg" src={info.getValue()} />),
            header: "تصویر",
            inExport: false
        },
        {
            id: "product_name",
            header: "نام محصول",
            cell: (info) => (
                <div className="max-w-[200px] truncate" title={info.getValue()}>
                    {info.getValue() || "---"}
                </div>
            ),
        },
        {
            id: "product_sku",
            header: "کد محصول (SKU)",
            cell: (info) => info.getValue() || "---",
        },
        {
            id: "quantity",
            header: "تعداد",
            cell: (info) => info.getValue() || "---",
        },
        {
            id: "order_id",
            header: "شماره سفارش",
            cell: (info) => info.getValue(),
        },
        {
            id: "order_date",
            header: "تاریخ سفارش",
            cell: (info) => formatDate(info.getValue()),
        },
        {
            id: "order_status",
            header: "وضعیت سفارش",
            cell: (info) => (
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(info.getValue())}`}>
                    {getStatusText(info.getValue())}
                </span>
            ),
        },
        {
            id: "item_total",
            header: "مبلغ (تومان)",
            cell: (info) => (
                <span className="font-bold">
                    {formatPrice(info.getValue())}
                </span>
            ),
        },
    ];

    return (
        <div>
            <PageHeader title="لیست سفارشات سلرها" />

            <div className="mt-6 w-full">
                <div className="flex justify-between w-full mb-6">
                    <div className="flex items-center gap-4">
                        <label className="text-sm font-medium text-gray-700">
                            انتخاب سلر:
                        </label>
                        <select
                            value={selectedSeller?.email || ""}
                            onChange={(e) => {
                                const seller = users.find(user => user.email === e.target.value);
                                setSelectedSeller(seller);
                            }}
                            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[250px]"
                        >
                            <option value="">انتخاب کنید...</option>
                            {users.map((user) => (
                                <option key={user.id} value={user.email}>
                                    {user.fullName} ({user.email})
                                </option>
                            ))}
                        </select>
                    </div>
                    <FilterBox width={350} title={<span className="flex gap-1 items-center"><MdAccessTime size={20} />بازه زمانی:</span>} value={`از ${timePeriod?.[0] ? timePeriod?.[0] : "ابتدا"} تا ${timePeriod?.[1] ? timePeriod?.[1] : "کنون"}`}>
                        <div className="flex flex-col gap-2">
                            <DatePicker
                                style={{
                                    width: "100%",
                                    height: "36px",
                                    borderRadius: "10px",
                                    border: "1px solid #e5e7eb",
                                }}
                                placeholder={"از تاریخ ..."}
                                onChange={(value) => {
                                    setTimePeriod((old) => [
                                        persianToEnglishDigitConverter(value?.format("YYYY/MM/DD")),
                                        old?.[1],
                                    ]);
                                    // setFilterValue((old) => [
                                    //     persianToEnglishDigitConverter(value?.format("YYYY/MM/DD")),
                                    //     old?.[1],
                                    // ]);
                                }}
                                calendar={persian}
                                locale={persian_fa}
                                format="YYYY/MM/DD"
                                className="rmdp-mobile"
                            />
                            <DatePicker
                                style={{
                                    width: "100%",
                                    height: "36px",
                                    borderRadius: "10px",
                                    border: "1px solid #e5e7eb",
                                }}
                                placeholder={"تا تاریخ ..."}
                                onChange={(value) => {
                                    setTimePeriod((old) => [
                                        old?.[0],
                                        persianToEnglishDigitConverter(value?.format("YYYY/MM/DD")),
                                    ]);
                                    // setFilterValue((old) => [
                                    //     persianToEnglishDigitConverter(value?.format("YYYY/MM/DD")),
                                    //     old?.[1],
                                    // ]);
                                }}
                                calendar={persian}
                                locale={persian_fa}
                                format="YYYY/MM/DD"
                                className="rmdp-mobile"
                            />
                        </div>
                    </FilterBox>


                </div>

                {selectedSeller && (
                    <Table
                        data={salesData}
                        columns={columns}
                        tableName="فروش سلرها"
                        CSVExportPermission={true}
                    />
                )}

                {!selectedSeller && (
                    <div className="text-center py-8 text-gray-500">
                        لطفاً یک سلر انتخاب کنید
                    </div>
                )}
            </div>
        </div>
    );
}